import React from 'react'
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material'
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable
} from '@tanstack/react-table'

import { PaymentReceiptTableDataProps } from './PaymentSearch'


// Column helper for type safety
const columnHelper = createColumnHelper<PaymentReceiptTableDataProps>()

// Define columns
const columns = [
  columnHelper.accessor('company_name', {
    header: 'Company Name',
    cell: (info) => (
      <Typography variant='body2' fontWeight='medium'>
        {info.getValue()}
      </Typography>
    )
  }),
  columnHelper.accessor('receipt_number', {
    header: 'Recipt Number',
    cell: (info) => (
      <Typography variant='body2' fontFamily='monospace'>
        {info.getValue()}
      </Typography>
    )
  }),
  columnHelper.accessor('receipt_date', {
    header: 'Recipt Date',
    cell: (info) => (
      <Typography variant='body2' fontFamily='monospace'>
        {new Date(info.getValue()).toLocaleDateString()}
      </Typography>
    )
  }),
  columnHelper.accessor('mode_of_payment', {
    header: 'Mode of Payment',
    cell: (info) => (
      <Typography variant='body2' fontWeight='600'>
        {info.getValue()}
      </Typography>
    )
  }),
  columnHelper.accessor('outstanding_dues', {
    header: 'Outstanding Dues',
    cell: (info) => (
      <Typography variant='body2' fontWeight='600'>
        {info.getValue()}
      </Typography>
    )
  }),
  columnHelper.accessor('particulars', {
    header: 'Particulars',
    cell: (info) => (
      <Typography variant='body2' fontWeight='600'>
        {info.getValue()}
      </Typography>
    )
  }),
  columnHelper.accessor('receipt_currency', {
    header: 'Receipt Currency',
    cell: (info) => (
      <Typography variant='body2' fontWeight='600'>
        {info.getValue()}
      </Typography>
    )
  }),
  columnHelper.accessor('receipt_amount', {
    header: 'Receipt Amount',
    cell: (info) => (
      <Typography variant='body2' fontWeight='600'>
        {info.getValue()}
      </Typography>
    )
  }),
  columnHelper.accessor('created_by', {
    header: 'Created By',
    cell: (info) => (
      <Typography variant='body2' fontWeight='600'>
        {info.getValue()}
      </Typography>
    )
  }),
  columnHelper.accessor('created_on', {
    header: 'Created On',
    cell: (info) => (
      <Typography variant='body2' fontWeight='600'>
        {new Date(info.getValue()).toLocaleDateString()}
      </Typography>
    )
  }),
  columnHelper.accessor('action', {
    header: 'Action',
    cell: (info) => {
      const url = info.getValue() as string
      return url ? (
        <Typography variant='body2' fontWeight='600'>
          <a
            href={url}
            target='_blank'
            rel='noopener noreferrer'
            style={{ color: '#1976d2', textDecoration: 'underline' }}
          >
            View
          </a>
        </Typography>
      ) : null
    }
  })
]

export default function PaymentSearchTable({
  data
}: {
  data: PaymentReceiptTableDataProps[]
}) {
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel()
  })

  return (
    <Box sx={{ p: 3 }}>
      <TableContainer component={Paper} elevation={1}>
        <Table>
          <TableHead>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableCell
                    key={header.id}
                    sx={{
                      fontWeight: 'bold',
                      backgroundColor: 'grey.50',
                      py: 2
                    }}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableHead>
          <TableBody>
            {table.getRowModel().rows.map((row) => (
              <TableRow
                key={row.id}
                hover
                sx={{
                  '&:nth-of-type(odd)': {
                    backgroundColor: 'action.hover'
                  }
                }}
              >
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id} sx={{ py: 2.5 }}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {/* Empty state */}
        {table.getRowModel().rows.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <Typography color='text.secondary'>
              No payment found
            </Typography>
          </Box>
        )}
      </TableContainer>
    </Box>
  )
}
